/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Spin, notification } from 'antd';
import get from 'lodash/get';
import {
  fetchSpacerLogOverview,
  fetchSpacerLogDaily,
} from '../../services/spacerLogDashboardApi';
import { isAiInsightsUiEnabled, SPACER_LOG_AI_FEATURES } from '../../services/aiInsightsApi';
import SpacerLogOverviewView from './SpacerLogOverviewView';
import SpacerLogDailyView from './SpacerLogDailyView';
import SpacerLogDayToolbar from './SpacerLogDayToolbar';
import SpacerLogAiAskBar from './SpacerLogAiAskBar';
import {
  stepAnchorDate,
  resolveDailySowId,
} from './spacerLogUtils';
import '../../pages/dashboard/DashboardPage.css';
import './SpacerLogDashboard.css';

const SpacerLogDashboard = ({ selectedProject }) => {
  const projectId = get(selectedProject, 'id');
  const showAiInsights = isAiInsightsUiEnabled();

  const [activeView, setActiveView] = useState('project');
  const [sowId, setSowId] = useState('all');
  const [periodType, setPeriodType] = useState('month');
  const [anchorDate, setAnchorDate] = useState(moment().format('YYYY-MM-DD'));
  const [dailyDate, setDailyDate] = useState(null);
  const [dailySowId, setDailySowId] = useState(null);
  const [shiftFilter, setShiftFilter] = useState('both');

  const [overviewData, setOverviewData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [dailyError, setDailyError] = useState(null);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiView, setAiView] = useState('ask');
  const [aiFeature, setAiFeature] = useState(null);

  /** Primary Ask Evans entry — optional feature opens Insights on full page. */
  const openAiChat = useCallback((feature) => {
    if (feature) {
      setAiFeature(feature);
      setAiView('insights');
    } else {
      setAiView('ask');
    }
    setAiOpen(true);
  }, []);

  const loadOverview = useCallback(async () => {
    if (!projectId) return;
    setLoadingOverview(true);
    setOverviewError(null);
    try {
      const data = await fetchSpacerLogOverview({
        projectId,
        periodType,
        anchorDate,
        sowId,
      });
      setOverviewData(data);
    } catch (err) {
      const msg = get(err, 'error', get(err, 'message', 'Failed to load spacer log overview.'));
      setOverviewError(msg);
      notification.error({ message: msg });
    } finally {
      setLoadingOverview(false);
    }
  }, [projectId, periodType, anchorDate, sowId]);

  const loadDaily = useCallback(async () => {
    if (!projectId || !dailyDate || !dailySowId) return;
    setLoadingDaily(true);
    setDailyError(null);
    try {
      const data = await fetchSpacerLogDaily({
        projectId,
        sowId: dailySowId,
        logDate: dailyDate,
        shift: shiftFilter,
      });
      setDailyData(data);
    } catch (err) {
      const msg = get(err, 'error', get(err, 'message', 'Failed to load daily spacer log.'));
      setDailyError(msg);
      notification.error({ message: msg });
    } finally {
      setLoadingDaily(false);
    }
  }, [projectId, dailyDate, dailySowId, shiftFilter]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeView === 'daily' && dailyDate && dailySowId) {
      loadDaily();
    }
  }, [activeView, dailyDate, dailySowId, shiftFilter, loadDaily]);

  const meta = overviewData?.meta;
  const dataRange = meta?.dataRange;
  const pipelines = overviewData?.filters?.pipelines || [];

  const aiContext = useMemo(() => {
    const projectName =
      get(selectedProject, 'name')
      || get(selectedProject, 'projectName')
      || get(selectedProject, 'title')
      || null;

    const pipelineForDaily =
      pipelines.find((p) => String(p.sowId) === String(dailySowId)) || null;
    const pipelineForOverview =
      pipelines.find((p) => String(p.sowId) === String(sowId)) || null;

    const logDateStr = dailyDate ? String(dailyDate).slice(0, 10) : null;

    if (activeView === 'daily') {
      const sid = dailySowId != null && dailySowId !== 'all' ? Number(dailySowId) : null;
      return {
        projectId,
        projectName,
        dashboardScope: 'daily',
        sowId: sid != null && Number.isFinite(sid) && sid > 0 ? sid : null,
        logDate: logDateStr,
        shift: shiftFilter,
        fromDate: logDateStr,
        toDate: logDateStr,
        periodLabel: logDateStr,
        pipelineLabel: pipelineForDaily?.label || pipelineForDaily?.name || null,
      };
    }

    const overviewSow =
      sowId != null && sowId !== 'all' ? Number(sowId) : null;
    const dailySow =
      dailySowId != null && dailySowId !== 'all' ? Number(dailySowId) : null;
    const resolvedSow =
      overviewSow != null && Number.isFinite(overviewSow) && overviewSow > 0
        ? overviewSow
        : dailySow != null && Number.isFinite(dailySow) && dailySow > 0
          ? dailySow
          : null;

    return {
      projectId,
      projectName,
      dashboardScope: 'overview',
      sowId: resolvedSow,
      logDate: logDateStr,
      shift: logDateStr ? shiftFilter : null,
      periodType,
      anchorDate,
      fromDate: meta?.fromDate || null,
      toDate: meta?.toDate || null,
      periodLabel: meta?.periodLabel || null,
      pipelineLabel:
        sowId === 'all' || sowId == null
          ? pipelineForDaily?.label || pipelineForOverview?.label || 'All pipelines'
          : pipelineForOverview?.label || pipelineForOverview?.name || null,
    };
  }, [
    activeView,
    projectId,
    selectedProject,
    sowId,
    dailySowId,
    dailyDate,
    shiftFilter,
    periodType,
    anchorDate,
    meta?.fromDate,
    meta?.toDate,
    meta?.periodLabel,
    pipelines,
  ]);

  const handleDayClick = (date) => {
    const resolved = resolveDailySowId(sowId, pipelines);
    if (!resolved) {
      notification.warn({ message: 'Select a pipeline with spacer log data to view daily detail.' });
      return;
    }
    setDailyData(null);
    setDailyDate(date);
    setDailySowId(resolved);
    setShiftFilter('both');
    setActiveView('daily');
  };

  const handleLogDateChange = (date) => {
    if (!date) return;
    setDailyData(null);
    setDailyDate(date);
  };

  const handleDailySowChange = (nextSowId) => {
    setDailyData(null);
    setDailySowId(nextSowId);
  };

  const ensureDailyContext = () => {
    if (dailyDate && dailySowId) return;
    const last = dataRange?.lastLogDate;
    const resolved = resolveDailySowId(sowId, pipelines);
    if (last && resolved) {
      setDailyDate(last);
      setDailySowId(resolved);
    }
  };

  const dayToolbarProps = {
    logDate: dailyDate,
    onLogDateChange: (date) => {
      if (!dailySowId) {
        const resolved = resolveDailySowId(sowId, pipelines);
        if (resolved) setDailySowId(resolved);
      }
      handleLogDateChange(date);
    },
    pipelines,
    sowId: dailySowId,
    onSowChange: handleDailySowChange,
    dataRange,
    shiftFilter,
    onShiftFilterChange: setShiftFilter,
  };

  const canNextPeriod =
    meta?.toDate && dataRange?.lastLogDate
      ? moment(meta.toDate).isBefore(dataRange.lastLogDate, 'day')
      : false;

  const footerLabel = `SPACER LOG · PROJECT ${projectId || '—'}`;

  return (
    <div className="spacer-log-dashboard">
      <div className="spacer-log-dashboard__tabbar">
        <button
          type="button"
          className={`spacer-log-dashboard__tab-btn${activeView === 'project' ? ' spacer-log-dashboard__tab-btn--active' : ''}`}
          onClick={() => setActiveView('project')}
        >
          Project Overview
        </button>
        <button
          type="button"
          className={`spacer-log-dashboard__tab-btn${activeView === 'daily' ? ' spacer-log-dashboard__tab-btn--active' : ''}`}
          onClick={() => {
            ensureDailyContext();
            setActiveView('daily');
          }}
        >
          Daily Detail
        </button>
      </div>

      {activeView === 'daily' ? <SpacerLogDayToolbar {...dayToolbarProps} /> : null}

      <Spin
        spinning={activeView === 'project' ? loadingOverview : loadingDaily}
        tip={activeView === 'project' ? 'Loading project overview…' : 'Loading daily detail…'}
      >
        <div className="spacer-log-dashboard__content">
          {activeView === 'project' ? (
            overviewError ? (
              <div className="spacer-log-dashboard__error">{overviewError}</div>
            ) : overviewData ? (
              <SpacerLogOverviewView
                data={overviewData}
                sowId={sowId}
                periodType={periodType}
                onSowChange={setSowId}
                onPeriodTypeChange={setPeriodType}
                onPrevPeriod={() => setAnchorDate((d) => stepAnchorDate(d, periodType, -1))}
                onNextPeriod={() => setAnchorDate((d) => stepAnchorDate(d, periodType, 1))}
                canNextPeriod={canNextPeriod}
                onDayClick={handleDayClick}
                isLoading={loadingOverview}
              />
            ) : (
              <div className="spacer-log-dashboard__loading">
                No overview data returned from the API for this project and period.
              </div>
            )
          ) : dailyError ? (
            <div className="spacer-log-dashboard__error">{dailyError}</div>
          ) : dailyData || loadingDaily ? (
            <SpacerLogDailyView
              data={dailyData}
              shiftFilter={shiftFilter}
              onShiftFilterChange={setShiftFilter}
              isLoading={loadingDaily}
              showAiInsights={showAiInsights}
              onOpenAiInsight={openAiChat}
            />
          ) : (
            <div className="spacer-log-dashboard__loading">
              {dailyDate ? 'Loading daily detail…' : 'Select a day from the heatmap or open Daily Detail.'}
            </div>
          )}
        </div>
      </Spin>

      <footer className="spacer-log-dashboard__footer">
        <span>{footerLabel}</span>
        <span>{meta?.updatedAt ? `UPDATED ${meta.updatedAt}` : ''}</span>
      </footer>

      {showAiInsights ? (
        <SpacerLogAiAskBar
          open={aiOpen}
          onOpenChange={setAiOpen}
          view={aiView}
          onViewChange={setAiView}
          initialFeature={
            aiFeature
            || (activeView === 'project'
              ? SPACER_LOG_AI_FEATURES.PERIOD_BRIEF
              : SPACER_LOG_AI_FEATURES.SHIFT_BRIEF)
          }
          tabScope={activeView === 'project' ? 'overview' : 'daily'}
          context={aiContext}
          activeView={activeView === 'project' ? 'project' : 'daily'}
        />
      ) : null}
    </div>
  );
};

export default SpacerLogDashboard;
